<div class="fixed inset-0 z-[10000] flex items-center justify-center p-4 no-print" role="dialog" aria-modal="true" aria-labelledby="modify-title">
  <div class="absolute inset-0 bg-dark-text/60 backdrop-blur-sm" (click)="close()"></div>
  <div cntFocusTrap (escape)="close()" class="relative bg-white rounded-2xl shadow-[0_24px_48px_rgba(0,0,0,0.2)] max-w-lg w-full p-6 md:p-8 max-h-[90dvh] overflow-y-auto">
    <div class="inline-flex items-center justify-center w-14 h-14 rounded-full bg-jungle-green/10 mb-4">
      <span aria-hidden="true" class="material-symbols-outlined text-3xl text-jungle-green">edit_calendar</span>
    </div>
    <h2 id="modify-title" class="font-headline font-bold text-dark-text text-2xl tracking-tight mb-2">Modify your trip</h2>
    <p class="text-sm text-muted-text font-body leading-relaxed mb-6">
      @if (isRequestToBook) {
        Pick new dates or add-ons. Your host approves the change — until then, your current booking stands.
      } @else if (isReal) {
        Pick new dates or add-ons. Changes apply as soon as you save.
      } @else {
        Pick new dates or change your guest count. The total updates to match.
      }
    </p>

    <div class="mb-5 relative">
      <button type="button" (click)="toggleDatesEditor()"
        class="w-full text-left flex items-center gap-3 p-3 rounded-xl border transition-colors"
        [ngClass]="datesEditorOpen ? 'border-trinidad bg-trinidad/5' : 'border-dark-text/10 hover:border-trinidad/40'">
        <span aria-hidden="true" class="material-symbols-outlined text-jungle-green">calendar_today</span>
        <div class="flex-1 min-w-0">
          <div class="text-[0.65rem] font-label uppercase tracking-[0.1em] text-muted-text font-bold">Trip dates</div>
          <div class="text-sm font-body font-bold text-dark-text truncate">
            @if (range?.start && range?.end) {
              {{ range?.start | date:'MMM d' }} → {{ range?.end | date:'MMM d' }}
            } @else if (range?.start) {
              {{ range?.start | date:'MMM d' }} → pick check-out
            } @else {
              Tap to pick dates
            }
          </div>
        </div>
        <span aria-hidden="true" class="material-symbols-outlined text-base text-trinidad">edit</span>
      </button>
      @if (datesEditorOpen) {
        <!-- Backdrop limited to inside the modal so click-outside closes the popover, not the modal -->
        <div class="absolute inset-0 z-10" (click)="closeDatesEditor()" style="position: fixed; inset: 0;"></div>
        <div class="absolute left-0 right-0 mt-2 z-20 bg-white rounded-2xl border border-dark-text/10 shadow-[0_24px_48px_rgba(0,0,0,0.18)] p-4">
          <div class="flex items-center justify-between mb-2 px-1">
            <span class="text-[0.7rem] font-label uppercase tracking-[0.14em] text-dark-text font-bold">Pick dates</span>
            <button type="button" (click)="closeDatesEditor()" class="text-trinidad text-xs uppercase tracking-[0.12em] font-button font-bold hover:underline">Done</button>
          </div>
          <div class="flex justify-center">
            <mat-calendar [selected]="range" [minDate]="minDate" [maxDate]="maxDate" [dateFilter]="dateFilter" (selectedChange)="onDateSelected($event)" class="cnt-inline-calendar bg-transparent border-none"></mat-calendar>
          </div>
        </div>
      }
    </div>

    <!-- Real reservations have no guest-count field, so only mock bookings can change it. -->
    @if (!isReal) {
      <div class="flex items-center justify-between p-4 rounded-md bg-cream/40 border border-dark-text/8 mb-5">
        <div>
          <div class="text-xs font-label uppercase tracking-[0.1em] text-muted-text font-bold">Guests</div>
          <div class="text-sm font-body text-dark-text mt-0.5">How many will join you?</div>
        </div>
        <div class="flex items-center gap-3">
          <button aria-label="Remove a guest" type="button" (click)="stepGuests(-1)" [disabled]="guests <= 1"
            class="w-10 h-10 rounded-full border border-dark-text/15 flex items-center justify-center hover:border-trinidad disabled:opacity-40">
            <span aria-hidden="true" class="material-symbols-outlined text-base">remove</span>
          </button>
          <span class="font-headline font-bold text-dark-text text-lg w-6 text-center">{{ guests }}</span>
          <button aria-label="Add a guest" type="button" (click)="stepGuests(1)"
            class="w-10 h-10 rounded-full border border-dark-text/15 flex items-center justify-center hover:border-trinidad">
            <span aria-hidden="true" class="material-symbols-outlined text-base">add</span>
          </button>
        </div>
      </div>
    }

    @if (availableAddOns.length > 0) {
      <div class="mb-5">
        <div class="text-xs font-label uppercase tracking-[0.1em] text-muted-text font-bold mb-3 px-1">Add-ons</div>
        <ul class="space-y-2 max-h-[28vh] overflow-y-auto pr-1">
          @for (a of availableAddOns; track a.id) {
            <li>
              <button type="button" (click)="toggleAddOn(a.id)"
                class="w-full text-left flex items-center gap-3 p-3 rounded-md border transition-colors"
                [ngClass]="isAddOnSelected(a.id) ? 'border-trinidad bg-trinidad/5' : 'border-dark-text/10 hover:border-trinidad/40'">
                <span aria-hidden="true" class="material-symbols-outlined text-jungle-green shrink-0">{{ a.icon }}</span>
                <div class="flex-1 min-w-0">
                  <div class="text-sm font-body font-bold text-dark-text">{{ a.label }}</div>
                  <div class="text-xs text-muted-text font-body line-clamp-1">{{ a.description }}</div>
                </div>
                <div class="text-right shrink-0">
                  <div class="text-sm font-headline font-bold text-trinidad">{{ a.price | currency: 'USD' : 'symbol' : '1.0-2' }}</div>
                  <div class="text-[0.6rem] uppercase tracking-[0.1em] font-button font-bold text-muted-text">{{ a.unit }}</div>
                </div>
                <span aria-hidden="true" class="material-symbols-outlined text-base shrink-0"
                  [ngClass]="isAddOnSelected(a.id) ? 'text-trinidad' : 'text-muted-text/40'"
                  [style.font-variation-settings]="isAddOnSelected(a.id) ? '\'FILL\' 1' : null">
                  {{ isAddOnSelected(a.id) ? 'check_circle' : 'add_circle' }}
                </span>
              </button>
              @if (isAddOnSelected(a.id)) {
                <cnt-add-on-qty-stepper
                  class="block mt-2 ml-3"
                  [qty]="addOnQtyOf(a.id)"
                  [max]="addOnMax(a)"
                  [label]="a.label"
                  (step)="adjustAddOnQty(a, $event)"
                >
                  <span class="text-xs font-body text-muted-text">
                    = <span class="font-bold text-dark-text">{{ addOnLineTotal(a) | currency: 'USD' : 'symbol' : '1.2-2' }}</span>
                  </span>
                </cnt-add-on-qty-stepper>
              }
            </li>
          }
        </ul>
      </div>
    }

    @if (nights === 0) {
      <div class="rounded-md bg-trinidad/5 border border-trinidad/20 p-4 mb-6 text-xs text-trinidad font-body inline-flex items-center gap-2">
        <span aria-hidden="true" class="material-symbols-outlined text-base">error</span>
        Check-out must be after check-in.
      </div>
    } @else if (quoteError) {
      <div class="rounded-md bg-trinidad/5 border border-trinidad/20 p-4 mb-6 text-xs text-trinidad font-body inline-flex items-center gap-2" role="alert">
        <span aria-hidden="true" class="material-symbols-outlined text-base">error</span>
        {{ quoteError }}
      </div>
    } @else {
      <div class="rounded-md bg-jungle-green/5 border border-jungle-green/15 p-4 mb-6 text-sm font-body" aria-live="polite">
        @if (quote) {
          <div class="flex items-center justify-between">
            <span class="text-dark-text">{{ nights }} {{ nights === 1 ? 'night' : 'nights' }}</span>
            <span class="text-muted-text">{{ quote.nightsSubtotal | currency: 'USD' : 'symbol' : '1.2-2' }}</span>
          </div>
          @if (quote.extrasTotal > 0) {
            <div class="flex items-center justify-between mt-1">
              <span class="text-muted-text">Add-ons</span>
              <span class="text-muted-text">{{ quote.extrasTotal | currency: 'USD' : 'symbol' : '1.2-2' }}</span>
            </div>
          }
          @for (d of quote.discounts; track d.label) {
            <div class="flex items-center justify-between mt-1 text-jungle-green">
              <span>{{ d.label }}</span>
              <span>−{{ d.amount | currency: 'USD' : 'symbol' : '1.2-2' }}</span>
            </div>
          }
          @if (quote.taxTotal > 0) {
            <div class="flex items-center justify-between mt-1">
              <span class="text-muted-text">Taxes</span>
              <span class="text-muted-text">{{ quote.taxTotal | currency: 'USD' : 'symbol' : '1.2-2' }}</span>
            </div>
          }
        } @else if (!isReal) {
          <div class="flex items-center justify-between">
            <span class="text-dark-text">{{ nights }} {{ nights === 1 ? 'night' : 'nights' }} × {{ booking.pricePerNight | currency: 'USD' : 'symbol' : '1.2-2' }}</span>
            <span class="text-muted-text">{{ booking.pricePerNight * nights | currency: 'USD' : 'symbol' : '1.2-2' }}</span>
          </div>
          @if (addOnsTotal > 0) {
            <div class="flex items-center justify-between mt-1">
              <span class="text-muted-text">Add-ons</span>
              <span class="text-muted-text">{{ addOnsTotal | currency: 'USD' : 'symbol' : '1.2-2' }}</span>
            </div>
          }
        } @else if (!hasChanges) {
          <p class="text-muted-text text-xs">Pick new dates or change your add-ons to see the new price.</p>
        } @else {
          <p class="text-muted-text text-xs inline-flex items-center gap-2">
            <span aria-hidden="true" class="material-symbols-outlined text-base animate-spin">progress_activity</span>
            Pricing your new dates…
          </p>
        }

        @if (hasChanges && priceIsFinal) {
          <div class="flex items-center justify-between mt-3 pt-3 border-t border-jungle-green/15">
            <span class="font-headline font-bold text-dark-text">New total</span>
            <span class="font-headline font-bold text-trinidad text-xl">{{ newTotal | currency: 'USD' : 'symbol' : '1.2-2' }}</span>
          </div>
          <div class="flex items-center justify-between mt-1 text-xs">
            <span class="text-muted-text">Current total</span>
            <span class="text-muted-text">{{ booking.total | currency: 'USD' : 'symbol' : '1.2-2' }}</span>
          </div>
          @if (isReal && difference !== 0) {
            <p class="mt-3 text-xs font-body leading-relaxed" [ngClass]="difference > 0 ? 'text-dark-text' : 'text-jungle-green'">
              @if (difference > 0) {
                <strong>{{ difference | currency: 'USD' : 'symbol' : '1.2-2' }} more</strong> — any CurbNTurf Cash you have is applied first, then your card on file is charged{{ isRequestToBook ? ' once the host approves' : '' }}.
              } @else {
                <strong>{{ -difference | currency: 'USD' : 'symbol' : '1.2-2' }} less</strong> — the difference comes back to you as CurbNTurf Cash{{ isRequestToBook ? ' once the host approves' : '' }}.
              }
            </p>
          }
        }
      </div>
    }

    <div class="flex flex-col-reverse sm:flex-row gap-3 justify-end">
      <button type="button" (click)="close()" [disabled]="saving"
        class="px-5 py-2.5 rounded-full bg-white border border-dark-text/15 text-dark-text text-xs uppercase tracking-[0.12em] font-button font-bold hover:border-dark-text disabled:opacity-50">
        Cancel
      </button>
      <button type="button" (click)="save()" [disabled]="saving || !canSave"
        class="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-trinidad text-white text-xs uppercase tracking-[0.12em] font-button font-bold hover:opacity-95 disabled:opacity-50 disabled:cursor-not-allowed">
        @if (saving) {
          <span aria-hidden="true" class="material-symbols-outlined text-base animate-spin">progress_activity</span>
          Saving…
        } @else if (isRequestToBook) {
          Request change
        } @else {
          Save changes
        }
      </button>
    </div>
  </div>
</div>
